import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Select, Modal, Text, ActionIcon } from "rizzui";
import { PiTrashBold } from "react-icons/pi";
import SettingsRepository from "@/utilities/repositories/Settings";
import { IDomainDTO } from "@/utilities/types/Setting";
import toast from "react-hot-toast";

export type SelectOptionType = {
  label: string;
  value: string;
  primary: boolean;
  configID: string;
};

interface ApiError {
  message?: string;
  response?: {
    data?: {
      message?: string;
    };
  };
}

type SelectDomainProps = {
  options: SelectOptionType[];
  defaultOptions: SelectOptionType | null;
  configID: IDomainDTO[];
  reset?: () => void;
  className?: string;
};

export default function SelectDomain({
  options,
  defaultOptions,
  configID,
  reset,
  className,
}: SelectDomainProps) {
  const [value, setValue] = useState(defaultOptions);
  const [searchValue, setSearchValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [domainToDelete, setDomainToDelete] = useState<SelectOptionType | null>(
    null,
  );

  useEffect(() => {
    setValue(defaultOptions);
  }, [defaultOptions]);

  const filteredOptions = useMemo(() => {
    if (!searchValue) return options;
    const search = searchValue.toLowerCase();
    return options.filter((item) => item.label.toLowerCase().includes(search));
  }, [searchValue, options]);

  const showAddButton = filteredOptions.length === 0 && searchValue.trim();

  const onChangeHandler = useCallback(
    async (selected: SelectOptionType) => {
      const previousValue = value;
      setValue(selected);
      setIsLoading(true);

      try {
        await SettingsRepository.updateDomain(selected.value, {
          primary: true,
        });
        toast.success("Updated User Domain");
        reset?.();
      } catch (error: unknown) {
        const apiError = error as ApiError;
        toast.error(apiError?.message || "Failed to update domain");
        setValue(previousValue);
      } finally {
        setIsLoading(false);
      }
    },
    [value, reset],
  );

  const onSubmitDomain = useCallback(async () => {
    const trimmedDomain = searchValue.trim().toLowerCase();

    if (!trimmedDomain) {
      toast.error("Domain name cannot be empty");
      return;
    }

    if (!configID?.length) {
      toast.error("Configuration not found");
      return;
    }

    setIsLoading(true);
    try {
      await SettingsRepository.createDomain({
        configId: configID[0].configId,
        name: trimmedDomain,
        primary: true,
      });
      toast.success("Domain created successfully");
      reset?.();
    } catch (error: unknown) {
      const apiError = error as ApiError;
      const errorMessage =
        apiError?.response?.data?.message ||
        apiError?.message ||
        "Failed to create domain";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [searchValue, configID, reset]);

  const openDeleteModal = useCallback(
    (e: React.MouseEvent, domain: SelectOptionType) => {
      e.stopPropagation();
      e.preventDefault();
      setDomainToDelete(domain);
      setDeleteModalOpen(true);
    },
    [],
  );

  const closeDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setDomainToDelete(null);
  }, []);

  const onDeleteDomain = useCallback(async () => {
    if (!domainToDelete) return;

    // Prevent deleting primary domain if it's the only one
    if (domainToDelete.primary && options.length === 1) {
      toast.error("Cannot delete the only domain");
      closeDeleteModal();
      return;
    }

    setIsLoading(true);
    const toastId = toast.loading("Deleting domain...");

    try {
      await SettingsRepository.deleteDomain(domainToDelete.value);
      toast.success("Domain deleted successfully", { id: toastId });
      closeDeleteModal();
      reset?.();
    } catch (error: unknown) {
      const apiError = error as ApiError;
      const errorMessage =
        apiError?.response?.data?.message ||
        apiError?.message ||
        "Failed to delete domain";
      toast.error(errorMessage, { id: toastId });
    } finally {
      setIsLoading(false);
    }
  }, [domainToDelete, options.length, closeDeleteModal, reset]);

  // Custom option renderer with delete button
  const getOptionDisplayValue = useCallback(
    (option: SelectOptionType) => (
      <div className="flex w-full items-center justify-between gap-2">
        <span className="truncate">{option.label}</span>
        <div className="flex items-center gap-1">
          {option.primary && (
            <span className="text-xs bg-green-200 text-green-900 px-1.5 py-0.5 rounded">
              Active
            </span>
          )}
          {!option.primary && (
            <ActionIcon
              size="sm"
              variant="text"
              className="text-red-500 hover:text-red-600 hover:bg-red-50"
              onClick={(e) => openDeleteModal(e, option)}
              title="Delete domain"
            >
              <PiTrashBold className="h-4 w-4" />
            </ActionIcon>
          )}
        </div>
      </div>
    ),
    [openDeleteModal],
  );

  return (
    <>
      <div className={`flex gap-3 items-center ${className || ""}`}>
        <Select
          value={value}
          label=""
          searchable
          options={options}
          onChange={onChangeHandler}
          onSearchChange={setSearchValue}
          disabled={isLoading}
          getOptionDisplayValue={getOptionDisplayValue}
          searchPrefix={
            showAddButton ? (
              <Button
                size="sm"
                variant="text"
                className="h-6 min-w-6 px-0 py-0 text-gray"
                onClick={onSubmitDomain}
                disabled={isLoading}
              >
                + Add new domain
              </Button>
            ) : null
          }
          searchPrefixClassName="translate-y-6 bottom-0 text-xs top-[unset] p-0"
          searchClassName="mb-5 pl-3"
        />
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModalOpen} onClose={closeDeleteModal}>
        <div className="m-auto px-7 pt-6 pb-8">
          <div className="mb-7 flex items-center justify-between">
            <Text tag="h3" className="text-lg font-semibold">
              Delete Domain
            </Text>
            <ActionIcon
              size="sm"
              variant="text"
              onClick={closeDeleteModal}
              className="hover:bg-gray-100"
            >
              ✕
            </ActionIcon>
          </div>

          <div className="mb-7">
            <Text className="text-gray-600">
              Are you sure you want to delete the domain{" "}
              <span className="font-semibold text-gray-900">
                {domainToDelete?.label}
              </span>
              ? This action cannot be undone.
            </Text>
          </div>

          <div className="flex items-center justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={closeDeleteModal}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              color="danger"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDeleteDomain();
              }}
              isLoading={isLoading}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
